import { Box, Typography } from '@mui/material'
import { FS_MICRO } from '../../common/theme'
import type { ChatIteration, ToolEvent } from '../../types'
import { MarkdownMessage } from '../../comps/MarkdownMessage'
import { ThinkingBlock, ToolTimeline, ToolBubbleConnector, BouncingDots } from './ChatTimeline'
import { TOOL_TEMPLATES } from './index'

interface IterationGroupProps {
  iter: ChatIteration
  isFinal: boolean
  stoppedManually?: boolean
  hasNext?: boolean
  hasPrev?: boolean
  defaultOpenThinking?: boolean
  // When true the bubble is always rendered (with bouncing-dot placeholder
  // if no text has streamed yet), and the thinking block defaults to open.
  streaming?: boolean
}

export function IterationGroup({ iter, isFinal, stoppedManually, hasNext, hasPrev, defaultOpenThinking, streaming }: IterationGroupProps) {
  // ChatIteration now only carries `sections`. Derive the legacy
  // tools/thinking/text fields the renderer used to read directly off
  // `iter`. Section id "0" is the general text bubble; section id
  // "thinking" carries the assistant's thinking trace; any section with a
  // `tool` becomes a tool event in the timeline.
  const sections = iter.sections ?? []
  const text = sections.find(s => s.id === '0')?.text ?? ''
  const thinking = sections.find(s => s.id === 'thinking')?.text ?? ''
  const tools: ToolEvent[] = sections
    .filter(s => !!s.tool)
    .map(s => ({
      tool: s.tool!,
      status: s.status ?? '',
      result_schema: s.result_schema,
      result: s.result,
    }))

  const hasTools = tools.length > 0
  const hasThinking = thinking.trim() !== ''
  const hasText = text !== ''
  // Tools that have a known result_schema get rendered as template cards
  // below the regular bubble/timeline. Each card shows a skeleton until the
  // tool's complete event lands its `result`, then swaps in the populated form.
  const templated = tools.filter(t => t.result_schema && TOOL_TEMPLATES[t.result_schema])
  const hasTemplates = templated.length > 0
  // Streaming iterations always show the bubble (dots if no text yet).
  const showBubble = !!streaming || hasText
  if (!hasTools && !hasThinking && !showBubble && !hasTemplates) return null

  const openThinking = streaming ? true : !!defaultOpenThinking
  const thinkingConnectsBelow = hasTools || showBubble || hasTemplates || hasNext
  // Tools always connect down now - the bubble (or templates) follow
  // immediately, so the assistant's bouncing-dots / final text appears at
  // the bottom of the iteration.
  const toolsConnectsBelow = hasTemplates || showBubble || hasNext

  return (
    <Box data-testid="chat_iteration">
      {hasThinking && (
        <ThinkingBlock
          thinking={thinking}
          defaultOpen={openThinking}
          connectsAbove={hasPrev}
          connectsBelow={thinkingConnectsBelow}
        />
      )}
      {hasTools && (
        <ToolTimeline
          tools={tools}
          connectsAbove={hasThinking || hasPrev}
          connectsToBubble={toolsConnectsBelow}
        />
      )}

      {/* Templated tool result cards (e.g. ExtractedQuestionsCard) */}
      {templated.map((t, i) => {
        const Template = TOOL_TEMPLATES[t.result_schema!]
        // Tool is "still streaming" if its complete event hasn't landed yet.
        const isStreaming = !t.result && t.status !== 'complete'
        return (
          <Box key={i} sx={{ mt: 0.5 }}>
            <Template result={t.result} streaming={isStreaming} />
          </Box>
        )
      })}

      {/* Assistant bubble - kept at the bottom of the iteration so the
          BouncingDots placeholder is always the last thing visible while
          the model is thinking. Once text arrives it streams into this
          same bubble in place. */}
      {showBubble && (
        <Box data-testid="chat_assistant_bubble" sx={{
          px: 1.5, py: 1,
          borderRadius: '16px 16px 16px 4px',
          bgcolor: 'action.selected',
          color: 'text.primary',
        }}>
          {hasText
            ? <MarkdownMessage content={text} />
            : <BouncingDots />}
          {isFinal && stoppedManually && (
            <Box sx={{ mt: 0.75, pt: 0.5, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ fontSize: FS_MICRO, opacity: 0.55, fontStyle: 'italic' }}>User Interrupted</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Bridge into the next iteration when there's more below. */}
      {hasNext && <ToolBubbleConnector />}
    </Box>
  )
}
